import { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import { ITenantHome, TenantHomeRequest } from "../library/models/tenantHome";
import { BaseService } from "./baseService";

/**
 * The clinic's front-page copy, as its administrator edits it.
 *
 * Neither JSON call names a clinic: the API takes it from the session, so this returns the
 * caller's own page and writes to nobody else's. Reading needs any signed-in member; writing
 * is the clinic admin's alone, and the API refuses either way rather than trusting the tab to
 * have been hidden.
 *
 * <h2>These are the editor's routes, not the page's</h2>
 *
 * `/home` itself never calls anything here. What a visitor sees is resolved from the
 * **hostname**, through `publicTenant.getTenantHome`, and it stays that way when they sign in
 * -- signing in does not change what the front page says. So this class exists for exactly one
 * screen, the console's Home tab, which is also the only place a clinic id could come from
 * while an admin sits on the platform's own address or on localhost.
 *
 * The hero photograph's upload is multipart and its download is bytes, so neither is a JSON
 * call -- but both live here anyway, for the reason `TenantBrandingService` gives about its
 * own two images: a second class for "the same page, in a different content type" would be
 * filing by encoding rather than by subject.
 */
export class TenantHomeService extends BaseService {
    getTenantHome(): UseQueryOptions<ITenantHome, Error> {
        return this.createGetQuery<ITenantHome>('/tenant-home');
    }

    /**
     * Sends the whole page, not a patch: whatever is blank goes back to the copy the
     * application ships, and the order of the card list is the order it is drawn in. The hero
     * photograph is not part of this and is untouched by it.
     *
     * Hand-written rather than built with createMutation, which only speaks POST/PATCH/DELETE
     * shapes. Resolves with the stored result, so the caller can render what was actually
     * saved instead of trusting its own draft.
     */
    saveTenantHomeMutation(): UseMutationOptions<ITenantHome, Error, TenantHomeRequest> {
        return {
            mutationFn: (payload: TenantHomeRequest): Promise<ITenantHome> =>
                this.put<ITenantHome>('/tenant-home', payload),
        };
    }

    /**
     * Fetches the stored hero back as a file, so the crop dialog can reopen it.
     *
     * Not a React Query option object like the read above: this is a one-shot fetch fired by a
     * button, not state a page renders, and caching it would hand an admin who re-crops just
     * after a save the previous image.
     *
     * The ETag rides as `?v=` because the path is stable across uploads and the response is
     * cached for five minutes -- without it, editing returns the hero before last.
     */
    async fetchHeroFile(tenantId: number, etag: string | null): Promise<File> {
        const query: string = etag ? `?v=${etag}` : '';

        const blob: Blob = await this.get<Blob>(`/tenant-home/${tenantId}/hero${query}`, {
            responseType: 'blob',
        });

        return new File([blob], 'hero.jpg', { type: blob.type || 'image/jpeg' });
    }

    /**
     * Uploads a hero image.
     *
     * `Content-Type: undefined` is not a mistake -- see `TenantBrandingService.uploadLogoMutation`.
     * This axios instance defaults every request to `application/json`, and a multipart body
     * needs a boundary only the browser can generate; clearing the header is what lets it.
     */
    uploadHeroMutation(): UseMutationOptions<void, Error, File> {
        return {
            mutationFn: (file: File): Promise<void> => {
                const body = new FormData();
                body.append('file', file);

                return this.put<void>('/tenant-home/hero', body, {
                    headers: { 'Content-Type': undefined },
                });
            },
        };
    }

    /** Removes the photograph; the hero falls back to its plain band. Copy is untouched. */
    removeHeroMutation(): UseMutationOptions<void, Error, void> {
        return {
            mutationFn: (): Promise<void> => this.delete<void>('/tenant-home/hero'),
        };
    }
}
